/**
 * Referee flow — API types.
 *
 * The generated contract + typed fetch client now come from `@bymyword/api-types`
 * (openapi-typescript output over the ByMyWord.Api OpenAPI 3 document, rec 6). This module
 * re-exports that generated surface (`paths`/`components`/`operations` + `refereeClient` /
 * `ApiError`) — it is the single seam `api-client.http.ts` builds its transport on.
 *
 * The generated document types the landed referee/audio/draft/sign endpoints in BOTH directions
 * — request bodies and 200 responses — and the http client consumes those component schemas
 * directly, with the wire→view-model mapping in `referee-wire.ts` (WP-F4/F5, landed; no stub
 * casts remain). The referee-facing VIEW-MODEL the SPA renders (`RefereeContext`, `DraftState`,
 * `SignResult`, …) still lives here because it is the app's own model — consumed by the mock
 * seam, the `RefereeApiClient` interface and every screen — not a restatement of the wire.
 *
 * The tokenized link is `selector.verifier` — it authenticates the referee and IS the session.
 * It is passed to the api-client, never stored in these payloads (and must never leave the app;
 * see src/lib/sentry.ts for the scrubbing that enforces that).
 */

// Generated OpenAPI contract + typed fetch client — the single source of truth for the wire
// shapes and the transport the real (non-mock) api-client uses.
export type { components, operations, paths } from '@bymyword/api-types';
export { ApiError, REFEREE_TOKEN_HEADER, refereeClient } from '@bymyword/api-types';
export type { ApiClient, ProblemDetails, RefereeClientOptions } from '@bymyword/api-types';

/**
 * The referee-relevant subset of `reference_requests.status`. The candidate-side states
 * (sent/opened/unlocked/…) collapse to `invited` here — the referee only ever sees their own leg.
 */
export type ReferenceStatus =
  // active, in-flow
  | 'invited' // link opened, not yet accepted → Landing
  | 'accepted' // accepted, details not confirmed → Confirm
  | 'details_confirmed' // ready to record → Record
  | 'recorded' // audio uploaded, pipeline not finished → Processing
  | 'processing' // AI pipeline running → Processing
  | 'drafted' // draft ready for review → Review
  | 'signed' // signed + delivered → Done / Already-signed
  // holds & terminals (calm paper pages)
  | 'needs_review' // moderation hold → Under review
  | 'rerecord_requested' // quality gate asked for a re-record → Record (with a note)
  | 'declined' // referee declined → Declined
  | 'expired' // link expired → Expired
  | 'withdrawn' // candidate cancelled → Withdrawn
  | 'failed'; // operational failure being re-driven → Under review (calm)

export type RefereeRelationship = 'manager' | 'colleague' | 'client' | 'mentor' | 'direct_report' | 'other';

/** Who is asking — shown on the landing. Never includes the candidate's contact details. */
export interface CandidateSummary {
  /** Display name, e.g. "Jane Doe". */
  name: string;
  /** Two-letter avatar initials, e.g. "JD". */
  initials: string;
  /** Optional role/headline, e.g. "Product designer · Northwind Studio". */
  headline?: string;
  /** Optional avatar image URL (same-site upload host). */
  photoUrl?: string;
  /** The candidate's optional personal note, shown on the landing. User content. */
  note?: string;
}

/** The details that appear in the letter — pre-filled from the candidate, confirmed by the referee. */
export interface RefereeDetails {
  name: string;
  /** Role at the time, e.g. "Former Manager". */
  roleThen?: string;
  company?: string;
  relationship?: RefereeRelationship;
}

/**
 * The neutral decline-reason chip tokens (ux-flows §2 step 2). These are the wire vocabulary the
 * backend persists to `reference_requests.decline_reason` and returns in the landing context — kept
 * verbatim so a chip the referee taps round-trips unchanged.
 */
export type DeclineReasonToken = 'no_time' | 'not_comfortable' | 'dont_know_well' | 'prefer_not';

/** The chip labels the landing shows, in the order the design mock presents them. */
export const DECLINE_REASON_LABELS: Record<DeclineReasonToken, string> = {
  no_time: "Don't have time",
  not_comfortable: "Don't feel I can",
  dont_know_well: "Don't know them well enough",
  prefer_not: 'Prefer not to',
};

/** Canonical chip order, used when the server offers no explicit ordering. */
export const DECLINE_REASON_ORDER: DeclineReasonToken[] = [
  'no_time',
  'not_comfortable',
  'dont_know_well',
  'prefer_not',
];

/** The resolved session context for a tokenized link. */
export interface RefereeContext {
  /** Opaque reference id (NOT the token). Safe to log/display. */
  referenceId: string;
  status: ReferenceStatus;
  candidate: CandidateSummary;
  referee: RefereeDetails;
  /** ~3-minute recording cap, in seconds. */
  maxRecordingSeconds: number;
  /** Regenerations left at draft review (tone presets / redo). */
  redraftsRemaining: number;
  /** The neutral decline chips the landing offers (server-provided order). */
  declineReasons?: DeclineReasonToken[];
  /**
   * The CURRENT attestation document the referee must consent to, surfaced by `/context`. The
   * consent POST must echo this exact key + version or the backend rejects it (422
   * `consent_version_mismatch`); absent when the wire didn't carry it (older backend / partial ctx).
   */
  consentDocumentKey?: string;
  consentDocumentVersion?: string;
  /** Public verification code, present once `signed`. */
  verificationCode?: string;
  /** ISO-8601 sign timestamp, present once `signed`. */
  signedAt?: string;
}

/**
 * The attestation a `recordConsent` call attests to — the key + version the backend surfaced via
 * `/context`. Posting a stale version is rejected (422 `consent_version_mismatch`), so callers pass
 * the pair straight from the resolved context rather than a hard-coded constant.
 */
export interface ConsentAttestation {
  documentKey: string;
  version: string;
}

export interface DeclineRequest {
  /** Optional neutral reason chip; never required (ux-flows §2 step 2). */
  reason?: DeclineReasonToken;
}

export interface ConfirmDetailsRequest {
  name: string;
  roleThen?: string;
  company?: string;
  relationship?: RefereeRelationship;
}

/**
 * Presigned single-PUT upload ticket. `audio/init` is called AFTER stop() with the exact byte
 * length; the PUT is signed with an exact Content-Length (WP-B12). The app must send the bytes
 * with exactly these headers.
 */
export interface AudioUploadTicket {
  uploadId: string;
  url: string;
  method: 'PUT';
  headers: Record<string, string>;
}

export interface AudioInitRequest {
  /** MediaRecorder-produced mime (e.g. "audio/mp4"). Stored as-produced, never transcoded. */
  contentType: string;
  /** Exact byte length of the recorded blob. */
  contentLength: number;
  /** Client-measured duration in seconds (server re-derives authoritatively). */
  durationSeconds: number;
}

export interface AudioCompleteRequest {
  /** The storage key handed back by `initAudioUpload` (the ticket's `uploadId`). */
  uploadId: string;
  /**
   * Exact byte length of the PUT blob. The backend re-verifies it via a HEAD as the authoritative
   * size gate (WP-B12) and rejects a mismatch — so it must be the true recorded size, never null.
   */
  sizeBytes: number;
  /** Client-measured duration in seconds (server re-derives authoritatively). Optional. */
  durationSeconds?: number;
  /** Gate-passing final turns from the optional server-created realtime session. */
  realtimeTranscript?: RealtimeTranscript;
}

export interface RealtimeSession {
  answerSdp: string;
  sessionId: string;
  sessionProof: string;
  expiresAt: string;
  model: string;
}

export interface RealtimeTranscript {
  sessionId: string;
  sessionProof: string;
  content: string;
}

export type LiveKeyPointsRequest = RealtimeTranscript;

/**
 * The referee-facing draft states (WP-B14 `RefereeDraftState`) — deliberately a SMALLER vocabulary
 * than `ReferenceStatus`: this is what the referee is told, not what the system is doing. Note the
 * backend folds `recorded`/`processing`/`failed` all into `processing`, because `failed` is an
 * operational holding state the sweeps re-drive — "still working on it" is the honest answer.
 */
export type DraftStatus = 'processing' | 'needs_review' | 'rerecord_requested' | 'drafted';

export interface DraftState {
  status: DraftStatus;
  /**
   * Extracted key points. The poll returns them as soon as extraction has produced them, which is
   * what makes the processing screen's progressive reveal real rather than a fake timer.
   * MAY BE EMPTY on a `needs_review` request: the `no_key_points` divert means extraction found
   * nothing usable and the drafter was never called.
   */
  keyPoints: string[];
  /**
   * The verbatim transcript, shown beside/below the draft for traceability. ONE string on the wire
   * — there are no timestamps or speaker turns to render (the design mock's "0:04" gutter has no
   * backing data; see `mapDraftResponse`). Absent until transcription has finished.
   */
  transcript?: string;
  /**
   * The drafted letter body, grounded only in the recording. Present ONLY when `status` is
   * `drafted` — content rides on the STATE, not on whether a row exists, so a `needs_review`
   * request's letter stays withheld even if one exists (that divert is what a human must clear).
   */
  letterBody?: string;
  /** Version of the letter in `letterBody`. Present only when `drafted`. */
  version?: number;
  /** Regenerations spent, derived server-side from the version chain — never trusted from here. */
  regenerationsUsed: number;
  /** What is left of the server-enforced cap, so the referee sees the limit coming. */
  regenerationsRemaining: number;
}

/**
 * The letter after an edit or a regeneration. Narrower than `DraftState` because the write
 * endpoints echo back only the letter + counters (`DraftWriteResponse`) — no state, transcript or
 * key points. The caller already knows the state is `drafted` (nothing else can be written to).
 */
export interface DraftWrite {
  letterBody: string;
  version: number;
  regenerationsUsed: number;
  regenerationsRemaining: number;
}

/**
 * The tone the letter is re-drafted in — the backend's `LetterTone` vocabulary VERBATIM, because
 * an unrecognised value is a 400 `invalid_tone`. The referee-facing labels ("Warmer" / "More
 * formal" / "Shorter", per the signed-off mock) are presentation and live on the review screen.
 */
export type LetterTone = 'professional' | 'warm' | 'concise';

export interface EditDraftRequest {
  letterBody: string;
}

export interface RegenerateDraftRequest {
  tone: LetterTone;
}

/**
 * The machine error codes the draft endpoints return in a `{ error }` body, surfaced on
 * `RefereeApiError.code`. Only the ones this surface must BRANCH on are named:
 *  - `regeneration_cap_reached` — a 409, deliberately NOT a 429: waiting will never help, the
 *    request has as many AI versions as it is ever going to have. The button is spent; EDITING IS
 *    STILL LIVE, so this must read differently from the letter being frozen outright.
 *  - `cannot_regenerate` / `cannot_edit` — the request has left `drafted` (most likely signed in
 *    another tab). The letter can no longer change at all: re-read the context and let the step
 *    guard move the referee on.
 *  - `draft_not_available` — a 409 from the poll: this screen has no business showing the request
 *    (never recorded, or already settled). Same handling: re-read context, leave.
 */
export const DRAFT_ERROR_CAP_REACHED = 'regeneration_cap_reached';
export const DRAFT_ERROR_CANNOT_REGENERATE = 'cannot_regenerate';
export const DRAFT_ERROR_CANNOT_EDIT = 'cannot_edit';
export const DRAFT_ERROR_NOT_AVAILABLE = 'draft_not_available';

/**
 * What the referee must be shown before signing, resolved server-side (`GET /api/referee/sign`).
 *
 * Nothing here is authored in the SPA — the attestation is a legal string published under a
 * version, and the signer name is the one the signature is checked against. Both come from the
 * backend so a signature can never attest to wording the referee was not shown, nor be checked
 * against a name this screen invented.
 */
export interface SignDisclosures {
  /**
   * The name the referee must type, EXACTLY as the server will compare it — their own confirmed
   * `referee_contacts.full_name`. Must be shown prominently: the server refuses a mismatch (422
   * `name_mismatch`), so a ceremony that does not show the expected name is a trap at the last step.
   */
  signerName: string;
  /** The exact attestation wording being agreed to, incl. the channel-not-authenticated note. */
  attestationText: string;
  /** The attestation document key + version, echoed back verbatim on sign (422 if stale). */
  documentKey: string;
  documentVersion: string;
}

export interface SignRequest {
  /** Full name typed to sign (rendered as the signature). This IS the signature. */
  typedName: string;
  /**
   * The attestation key + version taken from the disclosures response — echoed back so the server
   * can prove the signature is against the wording that was actually displayed. Never a constant:
   * the backend re-resolves the current document and rejects a mismatch with 422
   * `attestation_version_mismatch`.
   */
  documentKey: string;
  documentVersion: string;
}

export interface SignResult {
  /**
   * The PUBLIC verification code. CANONICAL FORM: 24 uppercase Crockford-Base32 characters, no
   * separators (120 bits of CSPRNG). Display it verbatim — the PDF groups it for print and the
   * signed-copy email groups it for reading, but that is THEIR presentation choice over the same
   * canonical value. Re-grouping or re-casing it here would show the referee a string that does not
   * match what a verify lookup is keyed on.
   */
  verificationCode: string;
  signedAt: string;
}

/**
 * The machine error codes the sign endpoints return in a `{ error }` body, surfaced on
 * `RefereeApiError.code`. Only the ones this surface must BRANCH on are named:
 *  - `name_mismatch` — a 422: the typed name is not the confirmed signer's name (compared after
 *    trim + whitespace-collapse + case-fold, but with NO accent or spelling folding). This is the
 *    one error that must read as an invitation to RETYPE, never as a failure — it lands at the
 *    emotional peak of the product, on someone doing a favour.
 *  - `attestation_version_mismatch` — a 422: the disclosures this page is showing are stale (the
 *    legal document was republished mid-session). The signature must not proceed against wording
 *    that is no longer current; re-fetch and show the new text.
 *  - `unknown_document` — a 422: no current attestation is published at all. Nothing the referee
 *    can do; this is an operational fault.
 *  - `cannot_sign` — a 409 from either verb: the request is not in a signable state (most likely
 *    signed in another tab, or withdrawn/expired under them). Re-read the context and let the step
 *    guard move them somewhere true.
 *  - `invalid_signature` — a 400: a blank/absent typed name or attestation pair.
 *
 * There is deliberately NO already-signed error code: signing an already-signed request is a 200
 * carrying the ORIGINAL verification code (the backend answers Signed and AlreadySigned
 * identically, on purpose — see `SignEndpoints`). A double-tap is a success, not a conflict.
 */
export const SIGN_ERROR_NAME_MISMATCH = 'name_mismatch';
export const SIGN_ERROR_ATTESTATION_STALE = 'attestation_version_mismatch';
export const SIGN_ERROR_UNKNOWN_DOCUMENT = 'unknown_document';
export const SIGN_ERROR_CANNOT_SIGN = 'cannot_sign';
export const SIGN_ERROR_INVALID_SIGNATURE = 'invalid_signature';

/**
 * A referee correcting their OWN name before signing (WP-B15c) — the escape the `name_mismatch`
 * 422 could not previously offer.
 *
 * The name was self-reported at confirm-details, so there is nothing to guard against here that was
 * not already true: they could have typed anything then. What is different is that this name goes
 * on a legal attestation, so the server records the change — including the name it replaced — on
 * the append-only timeline.
 */
export interface CorrectNameRequest {
  /** The corrected name. The server trims it; blank is a 400 `invalid_name`. */
  fullName: string;
}

export interface CorrectNameResult {
  /** The name now on record — what must be typed to sign. Re-render the ceremony against this. */
  signerName: string;
  /**
   * True when the name just REPLACED is still sitting in the letter body, so the letter and the
   * signature block would name two different people.
   *
   * This is a WARNING and never a block: the letter is the referee's own words (the draft editor
   * is open at exactly this status), and the name legitimately belongs in some bodies. The server
   * never rewrites the letter — surface a prompt to review the draft and let them decide.
   */
  previousNameInLetter: boolean;
}

/**
 * The name-correction error codes.
 *  - `invalid_name` — a 400: the submitted name was blank.
 *  - `cannot_correct_name` — a 409: not in a correctable state. Notably INCLUDES already-signed:
 *    the signature snapshot is immutable, so the name on it is final. Unlike a repeat sign (a 200
 *    carrying the original code), there is no sense in which a post-sign correction succeeded.
 */
export const CORRECT_NAME_ERROR_INVALID = 'invalid_name';
export const CORRECT_NAME_ERROR_CANNOT_CORRECT = 'cannot_correct_name';

/**
 * Uniform api-client error so callers can branch on gone/expired/not-found without HTTP leakage.
 *
 * `unauthorized` is a 401 from a post-accept verb (draft/sign/audio — the ones authenticated by
 * the elevated session COOKIE, not the emailed token) that the transport could not heal: it has
 * already re-posted `/accept` (the backend's re-entrant session re-mint) and retried once before
 * surfacing this. So by the time a screen sees it, the request has almost certainly left the
 * resumable states — treat it like a `conflict`: re-read the context and let the step guard move.
 */
export class RefereeApiError extends Error {
  constructor(
    message: string,
    readonly kind: 'not_found' | 'gone' | 'conflict' | 'rate_limited' | 'unauthorized' | 'network' | 'server',
    readonly status?: number,
    /**
     * The backend's machine error code when the body carried one (RFC7807-ish `{ error }`), e.g.
     * `consent_required` / `consent_version_mismatch` off a 422. Lets the flow branch on a specific
     * denial (route back to consent) without leaking HTTP status/URL. Absent for network failures.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'RefereeApiError';
  }
}
